/**
 * SAS query facts and URL-path -> `StorageResourceRef` mapping, shared by the
 * connection-string parser (bare SAS / public URLs). Process-agnostic.
 */
import type { StorageResourceRef, StorageService } from '../../types/azureStorage';
import { AZURITE_DEFAULT_PORTS } from './azureStorageUrl';
import { sasPathKindFromUrl } from './azureStorageFilePaths';

/** The SAS query parameters the probe ladder reads (spec §4.5); absent when the SAS omits them (policy SAS: `si` without `sp`). */
export type SasFacts = {
  expiresAt?: string;       // se
  permissions?: string;     // sp
  services?: string;        // ss (account SAS)
  resourceTypes?: string;   // srt (account SAS)
  signedResource?: string;  // sr (service SAS: c | b | d | s | f)
  policyId?: string;        // si (stored access policy)
  directoryDepth?: string;  // sdd (S8: directory SAS depth)
};

const SAS_FACT_KEYS: ReadonlyArray<[param: string, fact: keyof SasFacts]> = [
  ['se', 'expiresAt'],
  ['sp', 'permissions'],
  ['ss', 'services'],
  ['srt', 'resourceTypes'],
  ['sr', 'signedResource'],
  ['si', 'policyId'],
  ['sdd', 'directoryDepth'],
];

export function parseSasFacts(params: URLSearchParams): SasFacts {
  const facts: SasFacts = {};
  for (const [param, fact] of SAS_FACT_KEYS) {
    const value = params.get(param);
    if (value) facts[fact] = value;
  }
  return facts;
}

export function isAccountSasFacts(facts: SasFacts): boolean {
  return !!facts.services || !!facts.resourceTypes;
}

export type UrlService = StorageService | 'dfs';

/** Which service a URL addresses: the host's second label, or the Azurite port for path-style URLs (blob when unknown). */
export function serviceOfUrl(url: URL, pathStyle: boolean): UrlService | undefined {
  if (pathStyle) {
    const port = Number(url.port);
    const byPort = (Object.keys(AZURITE_DEFAULT_PORTS) as Array<keyof typeof AZURITE_DEFAULT_PORTS>).find(
      (service) => AZURITE_DEFAULT_PORTS[service] === port
    );
    return byPort ?? 'blob';
  }
  const label = url.hostname.split('.')[1]?.toLowerCase();
  return label === 'blob' || label === 'table' || label === 'queue' || label === 'file' || label === 'dfs' ? label : undefined;
}

/** Map decoded path segments (account segment already removed) to the resource they name; undefined when the shape is impossible. */
export function resourceFromUrlPath(service: UrlService, segments: string[], signedResource: string | undefined): StorageResourceRef | undefined {
  const [first, ...rest] = segments;
  const path = rest.join('/');
  switch (service) {
    case 'blob':
    case 'dfs':
      if (rest.length === 0) return { kind: 'container', container: first };
      if (signedResource === 'd' || service === 'dfs') {
        return { kind: 'hnsPath', fileSystem: first, path, isDirectory: signedResource !== 'b' };
      }
      return { kind: 'blob', container: first, name: path };
    case 'table':
      return rest.length === 0 ? { kind: 'table', table: first.replace(/\(\)$/, '') } : undefined;
    case 'queue':
      return rest.length === 0 ? { kind: 'queue', queue: first } : undefined;
    case 'file':
      if (rest.length === 0) return { kind: 'share', share: first };
      return signedResource === 'd' ? { kind: 'directory', share: first, path } : { kind: 'file', share: first, path };
  }
}

/**
 * S7 (Task 7.4, S7-10): a share URL's ref. `sharesnapshot=` names the snapshot; the path kind is `sasPathKindFromUrl`'s
 * (`sr=f` → file, a trailing '/' → directory). Any other path under a share SAS could be either: the `file` candidate
 * is answered with `pathKind: 'ambiguous'` — the form asks (preselected by `azure-storage:classify-share-path`) and
 * the saved scope is always explicit.
 */
export function shareResourceFromUrl(url: URL, segments: string[], pathStyle: boolean): { resource: StorageResourceRef; pathKind?: 'ambiguous' } | undefined {
  const [share, ...rest] = segments;
  if (!share) return undefined;
  const snapshot = url.searchParams.get('sharesnapshot')?.trim();
  const snap = snapshot ? { snapshot } : {};
  if (rest.length === 0) return { resource: { kind: 'share', share, ...snap } };
  const path = rest.join('/');
  const view = new URL(url.href);
  if (pathStyle) view.pathname = `/${url.pathname.split('/').slice(2).join('/')}`; // drop the account segment
  const kind = sasPathKindFromUrl(view);
  if (kind === 'directory') return { resource: { kind: 'directory', share, path, ...snap } };
  return { resource: { kind: 'file', share, path, ...snap }, ...(kind === 'ambiguous' ? { pathKind: 'ambiguous' as const } : {}) };
}

/** The URL's query minus `sharesnapshot=` (a ref field, never part of the stored SAS), in its original encoding. */
export function sasQueryOf(url: URL): string {
  return url.search.replace(/^\?/, '').split('&').filter((part) => part !== '' && !/^sharesnapshot=/i.test(part)).join('&');
}
